import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';

const fmtBRL = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return '-'; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
};

const STATUS_LABEL = {
  active: 'Ativo', trialing: 'Trial', pending_payment: 'Aguard. Pagamento',
  past_due: 'Inadimplente', suspended: 'Suspenso', canceled: 'Cancelado', pending: 'Pendente',
};
const STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  trialing: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  past_due: 'bg-orange-50 text-orange-700 border-orange-200',
  suspended: 'bg-rose-50 text-rose-700 border-rose-200',
  canceled: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
};

export default function DashboardHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.overview()
      .then(r => setData(r.data))
      .catch(e => console.error('Erro overview:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500" data-testid="dash-loading">Carregando...</div>;
  if (!data) return <div className="p-8 text-center text-rose-600">Erro ao carregar dashboard</div>;

  const k = data.kpis || {};
  const maxMrr = Math.max(...(data.grafico_mrr || []).map(m => m.valor || 0), 1);

  return (
    <div className="space-y-6" data-testid="dashboard-home">

      {/* KPIs principais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="MRR" value={fmtBRL(k.mrr)} accent="emerald" testid="kpi-mrr" />
        <KpiCard label="ARR" value={fmtBRL(k.arr)} accent="slate" testid="kpi-arr" />
        <KpiCard label="Ticket Médio" value={fmtBRL(k.ticket_medio)} accent="slate" testid="kpi-ticket-medio" />
        <KpiCard label="Recebido 30d" value={fmtBRL(k.total_recebido_30d)} accent="emerald" testid="kpi-recebido-30d" />
        <KpiCard label="Ativos" value={k.ativos ?? '-'} sub={`de ${k.total_tenants} total`} accent="emerald" testid="kpi-ativos" />
        <KpiCard label="Tickets" value={k.tickets_abertos ?? 0} sub="em aberto" accent={(k.tickets_abertos || 0) > 0 ? 'amber' : 'slate'} testid="kpi-tickets" />
      </div>

      {/* Segunda linha KPIs — status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Trial" value={k.trialing} color="text-blue-600" testid="mini-trial" />
        <MiniStat label="Aguard. Pagamento" value={k.pending_payment} color="text-amber-600" testid="mini-pending" />
        <MiniStat label="Inadimplentes" value={k.past_due} color="text-orange-600" testid="mini-past-due" />
        <MiniStat label="Vitalícios" value={k.lifetime} color="text-slate-600" testid="mini-lifetime" />
      </div>

      {/* Alerta MP se aplicável */}
      {data.mp_saude?.alerta && (
        <div
          className={`rounded-lg border p-4 flex items-start gap-3 ${
            data.mp_saude.alerta === 'critical' ? 'bg-rose-50 border-rose-300 text-rose-900' : 'bg-amber-50 border-amber-300 text-amber-900'
          }`}
          data-testid="mp-alert"
        >
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.008v.008H12v-.008Z" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold text-sm">
              {data.mp_saude.alerta === 'critical'
                ? 'Nenhum webhook do Mercado Pago recebido nos últimos 7 dias'
                : 'Último webhook do MP há mais de 3 dias'}
            </p>
            <p className="text-xs mt-1 opacity-90">
              Verifique se a URL do webhook está configurada corretamente no painel do Mercado Pago.
            </p>
          </div>
          <Link to="/financeiro" className="text-xs font-semibold underline whitespace-nowrap" data-testid="link-mp-details">
            Ver detalhes →
          </Link>
        </div>
      )}

      {/* Gráfico MRR + Últimos Pagamentos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" title="Faturamento — Últimos 6 meses" testid="widget-mrr-chart">
          <div className="flex items-end gap-2 h-40 pt-4 pb-2 px-1">
            {(data.grafico_mrr || []).map((m) => {
              const h = maxMrr > 0 ? Math.max((m.valor / maxMrr) * 100, 2) : 2;
              return (
                <div key={m.mes} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="text-[10px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    {fmtBRL(m.valor)}
                  </div>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-emerald-500 to-emerald-400 hover:from-emerald-600 hover:to-emerald-500 transition-colors"
                    style={{ height: `${h}%` }}
                    title={`${m.mes}: ${fmtBRL(m.valor)}`}
                  />
                  <div className="text-[10px] text-slate-500 font-medium">
                    {m.mes ? m.mes.split('-')[1] + '/' + m.mes.split('-')[0].slice(2) : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Últimos pagamentos" subtitle="(30 dias)" testid="widget-ultimos-pagamentos">
          {(data.ultimos_pagamentos || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">Nenhum pagamento nos últimos 30 dias</p>
          ) : (
            <ul className="divide-y divide-slate-100 -mx-4">
              {data.ultimos_pagamentos.slice(0, 6).map((p, i) => (
                <li key={i} className="px-4 py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate text-slate-800">{p.tenant_nome}</p>
                    <p className="text-[11px] text-slate-500">
                      {fmtDate(p.data)} · {(p.metodo || '').toUpperCase()}
                      {p.mp_payment_id && <span className="ml-1 text-emerald-600" title="Via Mercado Pago">●</span>}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-emerald-600 whitespace-nowrap">
                    {fmtBRL(p.valor)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Vencimentos + Tickets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card
          className="lg:col-span-2"
          title="Próximos vencimentos"
          subtitle="(7 dias)"
          actions={<Link to="/clientes" className="text-xs text-emerald-600 font-medium hover:underline">Ver todos →</Link>}
          testid="widget-vencimentos"
        >
          {(data.proximos_vencimentos || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">Nenhum vencimento nos próximos 7 dias</p>
          ) : (
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-100">
                    <th className="text-left px-4 py-2 font-medium">Cliente</th>
                    <th className="text-left px-4 py-2 font-medium">Vence em</th>
                    <th className="text-center px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">Dias</th>
                  </tr>
                </thead>
                <tbody>
                  {data.proximos_vencimentos.slice(0, 8).map((t) => (
                    <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`venc-${t.id}`}>
                      <td className="px-4 py-2 font-medium text-slate-800">{t.nome}</td>
                      <td className="px-4 py-2 text-slate-600 text-xs">{fmtDate(t.current_period_end)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[t.subscription_status] || 'bg-slate-100'}`}>
                          {STATUS_LABEL[t.subscription_status] || t.subscription_status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-xs font-bold">
                        <span className={t.dias_ate_vencer <= 2 ? 'text-rose-600' : t.dias_ate_vencer <= 5 ? 'text-amber-600' : 'text-slate-600'}>
                          {t.dias_ate_vencer !== null ? `${t.dias_ate_vencer}d` : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Tickets em aberto"
          actions={<Link to="/tickets" className="text-xs text-emerald-600 font-medium hover:underline">Ver todos →</Link>}
          testid="widget-tickets"
        >
          {(data.tickets_recentes || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">Nenhum ticket em aberto 🎉</p>
          ) : (
            <ul className="divide-y divide-slate-100 -mx-4">
              {data.tickets_recentes.map((t) => (
                <li key={t.id} className="px-4 py-2 text-sm">
                  <p className="font-medium truncate text-slate-800">{t.titulo}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className="truncate">{t.tenant_nome}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      t.prioridade === 'alta' ? 'bg-rose-50 text-rose-700' :
                      t.prioridade === 'media' ? 'bg-amber-50 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {t.prioridade}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Inadimplentes (só aparece se houver) */}
      {(data.inadimplentes || []).length > 0 && (
        <Card
          title={`Inadimplentes (${data.inadimplentes.length})`}
          subtitle="Clientes com pagamento em atraso"
          actions={<Link to="/clientes?filter=past_due" className="text-xs text-rose-600 font-medium hover:underline">Ver todos →</Link>}
          testid="widget-inadimplentes"
        >
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-100">
                  <th className="text-left px-4 py-2 font-medium">Cliente</th>
                  <th className="text-left px-4 py-2 font-medium">Venceu em</th>
                  <th className="text-center px-4 py-2 font-medium">Status</th>
                  <th className="text-right px-4 py-2 font-medium">Atraso</th>
                </tr>
              </thead>
              <tbody>
                {data.inadimplentes.map((t) => (
                  <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`inad-${t.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-800">{t.nome}</td>
                    <td className="px-4 py-2 text-slate-600 text-xs">{fmtDate(t.current_period_end)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[t.subscription_status] || 'bg-slate-100'}`}>
                        {STATUS_LABEL[t.subscription_status] || t.subscription_status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-xs font-bold text-rose-600">
                      {t.dias_atraso !== null ? `${t.dias_atraso}d` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="text-[10px] text-slate-400 text-right pt-2">
        Última atualização: {fmtDateTime(data.generated_at)}
      </p>
    </div>
  );
}

// ============ COMPONENTES AUXILIARES ============
function KpiCard({ label, value, sub, accent = 'slate', testid }) {
  const accentMap = {
    emerald: 'from-emerald-500/10 to-transparent border-emerald-200 text-emerald-700',
    amber: 'from-amber-500/10 to-transparent border-amber-200 text-amber-700',
    slate: 'from-slate-500/5 to-transparent border-slate-200 text-slate-700',
  };
  return (
    <div className={`bg-white rounded-lg border p-4 bg-gradient-to-br ${accentMap[accent] || accentMap.slate}`} data-testid={testid}>
      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function MiniStat({ label, value, color = 'text-slate-700', testid }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${color}`}>{value ?? '-'}</p>
    </div>
  );
}

function Card({ title, subtitle, actions, children, className = '', testid }) {
  return (
    <div className={`bg-white rounded-lg border border-slate-200 ${className}`} data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
